// import TableDataNotFound from "component/TableDataNotFound";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import strings from "translate/language";
import { AppointmentStatus } from "utils/constants";
import {
  formatAppointmentCellValue,
  getClosingStatusTextColor,
  getCheckInStatusTextColor,
} from "utils/appointmentListStatusDisplay";
import { handleListResponse, isEmpty, seo } from "utils/utils";
import {
  getAppointmentList,
  getAppointmentListForAdmin,
} from "../../reduxStore/action/appointmentActions";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
// import Pagination from "component/common/Pagination";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import { getAgentProperty } from "reduxStore/action/propertyActions";
import Select from "react-select";
// import usePage from "utils/usePage";
import ExcelJS from "exceljs";

const Appointment = () => {
  const navigate = useNavigate();
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const dispatch = useDispatch();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const appointmentListSelector = useSelector((state) => state.appointment);
  const ReportListSelector = useSelector((state) => state.report);
  const propertySelector = useSelector((state) => state.propertyData) || {};
  const { loading, response, response1 } = appointmentListSelector;

  const today = new Date();
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  // Local calendar YYYY-MM-DD (toISOString() uses UTC and shifts the day in IST and similar zones).
  const formatDate = (date) => moment(date).format("YYYY-MM-DD");

  const { view, create, edit, isAdmin } = usePermission({
    view: "view_appointment",
    create: "add_appointment",
    edit: "edit_appointment",
  });

  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    limit: "",
    offset: "",
    appointment_with: "",
    customer_name: "",
    status: "",
    property_name: "",
    site: "",
    search_by_mobile_number: "",
  });

  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [appointmentData, setAppointmentData] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [res, setRes] = useState(true);
  const [revistCheck, setRevistCheck] = useState(false);
  const [page, setPage] = useState(0);
  const hasCheckin = (checkinStatus) => {
    if (!checkinStatus) return false;
    if (Array.isArray(checkinStatus)) return checkinStatus.length > 0;
    return typeof checkinStatus === "object";
  };

  // Initial Fetch on Mount
  useEffect(() => {
    const queryStart = query.get("start_date");
    const queryEnd = query.get("end_date");
    const hiddenStatus = location.state?.hiddenStatus || "";

    const defaultStarts = formatDate(oneMonthAgo);
    const defaultEnds = formatDate(today);

    const defaultStart = queryStart || defaultStarts;
    const defaultEnd = queryEnd || defaultEnds;

    setForm({
      start_date: defaultStart,
      end_date: defaultEnd,
      limit: "",
      offset: "",
      appointment_with: "",
      customer_name: "",
      status: hiddenStatus,
      property_name: "",
      site: "",
      search_by_mobile_number: "",
      lead_priority: "",
      qualified: "",
    });

    setPage(0);
    setRes(true);

    if (isAdmin) {
      dispatch(
        getAppointmentListForAdmin({
          start_date: defaultStart,
          end_date: defaultEnd,
          status: hiddenStatus,
          noData: true,
        }),
      );
    } else {
      dispatch(
        getAppointmentList({
          start_date: defaultStart,
          end_date: defaultEnd,
          status: hiddenStatus,
          appointment_type: 2,
          limit: "",
          offset: 0,
        }),
      );
    }
  }, [dispatch, isAdmin]);

  // Response Handler
  useEffect(() => {
    const responseData = isAdmin ? response1 : response;
    if (responseData?.status === 200) {
      const data = responseData?.data || [];
      setRes(false);
      // Appointment Management: keep list stable & intuitive by sorting by Created Date (newest first).
      // Backend sorts by appointment_date/time which can place newly-created revisits in the middle.
      const safe = Array.isArray(data) ? data.slice() : [];
      safe.sort((a, b) => {
        const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
        const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
        if (tb !== ta) return tb - ta;
        // Tie-breaker: site visit time/date (desc) if createdDate missing/equal.
        const da = a?.appointment_date
          ? new Date(a.appointment_date).getTime()
          : 0;
        const db = b?.appointment_date
          ? new Date(b.appointment_date).getTime()
          : 0;
        if (db !== da) return db - da;
        return String(b?._id || "").localeCompare(String(a?._id || ""));
      });
      setAppointmentData(handleListResponse(safe) ? safe : []);
    } else {
      setRes(true);
      setAppointmentData([]);
    }
  }, [appointmentListSelector, isAdmin, response, response1, res]);

  // Admin Property List
  useEffect(() => {
    if (isAdmin) {
      const { reportPropertyList } = ReportListSelector;
      if (reportPropertyList?.status === 200) {
        setPropertyList(handleListResponse(reportPropertyList.data));
      } else {
        setPropertyList([]);
      }
    }
  }, [ReportListSelector, isAdmin]);

  // Non-Admin Property List Fix
  useEffect(() => {
    const agentResp = propertySelector.agentPropertyResponse;
    const adminResp = propertySelector.response;
    const currentResp = isAdmin ? adminResp : agentResp || adminResp;

    if (currentResp?.status === 200) {
      const filteredData = currentResp.data.filter(
        (item) => item.status === true,
      );
      setPropertyList(handleListResponse(filteredData || []));
    } else {
      setPropertyList([]);
    }
  }, [propertySelector, isAdmin]);

  // Dispatch property list based on role
  useEffect(() => {
    if (isAdmin) {
      dispatch(getReportPropertyList({ limit: "", offset: 0 }));
    } else {
      dispatch(getAgentProperty({ offset: 0, limit: "" }));
    }
  }, [dispatch, isAdmin]);

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;

    if (form.start_date && !form.end_date) {
      formError = true;
      err["end_date"] = strings.endDateErr || "End date is required";
    }

    setFormVaild(err);
    return formError;
  };

  const filterDetail = () => {
    if (!handleValid()) {
      const appointmentParams = {
        appointment_type: 2,
        limit: "",
        offset: 0,
        start_date: form.start_date,
        end_date: form.end_date,
        appointment_with: form.appointment_with,
        customer_name: form.customer_name,
        status: form.status,
        property_name: form?.site?.property_title,
        noData: true,
        search_by_mobile_number: form.search_by_mobile_number,
        qualified: form.qualified,
        lead_priority: form.lead_priority,
      };

      if (isAdmin) {
        setRevistCheck(form.status === "12");
        dispatch(getAppointmentListForAdmin(appointmentParams));
      } else {
        dispatch(getAppointmentList(appointmentParams));
      }

      setPage(0);
    }
  };

  const filterReset = () => {
    const defaultStart = formatDate(oneMonthAgo);
    const defaultEnd = formatDate(today);

    setForm({
      start_date: defaultStart,
      end_date: defaultEnd,
      appointment_with: "",
      customer_name: "",
      status: "",
      property_name: "",
      site: "",
      search_by_mobile_number: "",
      lead_priority: "",
      qualified: "",
    });

    setFormVaild({ end_date: "" });
    setPage(0);
    setRes(false);
    setRevistCheck(false);

    if (isAdmin) {
      dispatch(
        getAppointmentListForAdmin({
          start_date: defaultStart,
          end_date: defaultEnd,
          noData: true,
        }),
      );
    } else {
      dispatch(
        getAppointmentList({
          start_date: defaultStart,
          end_date: defaultEnd,
          appointment_type: 2,
          limit: "",
          offset: 0,
        }),
      );
    }
  };

  const handleInputField = (e) => {
    const updatedForm = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(updatedForm);

    if (e.target.name === "end_date") {
      setFormVaild({
        ...formVaild,
        end_date: "",
      });
    }
  };

  useEffect(() => {
    setEndDateDisable(form.start_date === "");
  }, [form.start_date]);

  useEffect(() => {
    seo({
      title: "Appointment Listing",
      metaDescription: "Appointment listing",
      canonicalLink: window.location.href,
    });
  }, []);

  const normalizeFollowupClosingStatus = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
    const followupAliases = new Set([
      "call",
      "whatsapp",
      "whats app",
      "call/whatsapp",
      "call / whatsapp",
      "next day call",
      "follow up",
      "follow-up",
      "follow up (call/whatsapp)",
    ]);
    return followupAliases.has(normalized) ? "Follow UP" : value;
  };

  /** Closing column: unchecked-in revisit has no disposition yet (keep blank). */
  const getClosingStatusLabel = (item, showAsVisited) => {
    const latestFollowupTitle = String(item?.last_followup_title || "")
      .trim()
      .toLowerCase();
    const isRevisitLike =
      Number(item?.status) === 2 || latestFollowupTitle === "revisit";
    if (isRevisitLike && !showAsVisited) {
      return "";
    }
    if (showAsVisited && Number(item?.status) === 2) {
      const cs = String(item?.closing_status || "")
        .trim()
        .toLowerCase();
      if (!cs || cs === "not updated") return "";
    }
    if (
      item?.closing_status !== null &&
      item?.closing_status !== undefined &&
      String(item.closing_status).trim() !== ""
    ) {
      return normalizeFollowupClosingStatus(item.closing_status);
    }
    if (showAsVisited && Number(item?.status) === 2) {
      return "";
    }
    return Number(item?.status) === 1
      ? moment() >=
        moment(
          moment(item.appointment_date).utc().format("DD-MM-yyyy") +
            " " +
            item.appointment_time,
          "DD-MM-yyyy hh:mm A",
        )
        ? "Notvisited"
        : "Upcoming"
      : Number(item?.status) === 3
        ? "Upcoming"
        : AppointmentStatus[item?.status];
  };

  // const handlePageClick = (newOffset) => {
  //   setPage(newOffset);
  //   dispatch(
  //     getAppointmentList({
  //       appointment_type: 2,
  //       limit: "",
  //       offset: newOffset,
  //       start_date: form.start_date,
  //       end_date: form.end_date,
  //       appointment_with: form.appointment_with,
  //       customer_name: form.customer_name,
  //       status: form.status,
  //       property_name: form?.site?.property_title,
  //       search_by_mobile_number: for,.search_by_mobile_number,
  //     })
  //   );
  // };

  const exportToExcel = async () => {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Appointments");

    // Define columns for Excel
    worksheet.columns = [
      { header: "Sr.No", key: "srno", width: 10 },
      { header: "Created Date & Time", key: "createdDate", width: 25 },
      { header: "Site Visit Time & Date", key: "siteVisit", width: 25 },
      { header: "Property Name", key: "property", width: 25 },
      { header: "Customer Name", key: "customer", width: 20 },
      { header: "Source", key: "source", width: 20 },
      { header: "Closing Status", key: "closingStatus", width: 20 },
      { header: "Check-In Status", key: "checkInStatus", width: 20 },
      { header: "Visiting Score", key: "visitingScore", width: 15 },
    ];

    // Map appointment data to rows
    const data = appointmentData.map((item, index) => {
      const chekin = hasCheckin(item?.checkin_status);
      // Also check site_visit_status from mapping
      const siteVisitStatus = item?.site_visit_status;
      const isVisitedFromMapping =
        siteVisitStatus === "Visited" ||
        siteVisitStatus === "Completed" ||
        siteVisitStatus === "Site Visit Done";

      // Convert the appointment date to a readable format
      const siteVisitTime = item.appointment_date
        ? moment(item.appointment_date).utc().format("DD-MM-yyyy") +
          " & " +
          (item.appointment_time || "")
        : "";
      const createdDate = item.createdDate
        ? moment(item.createdDate).format("DD-MM-yyyy hh:mm A")
        : "";
      const showAsVisited = chekin || isVisitedFromMapping;
      const closingRaw = getClosingStatusLabel(item, showAsVisited);
      const closingStatus = formatAppointmentCellValue(closingRaw);

      // Return row data
      return {
        srno: index + 1,
        createdDate: formatAppointmentCellValue(createdDate),
        siteVisit: formatAppointmentCellValue(siteVisitTime),
        property: formatAppointmentCellValue(item?.property_title),
        customer: formatAppointmentCellValue(
          `${item?.customer_first_name || ""} ${item?.customer_last_name || ""}`.trim(),
        ),
        source: formatAppointmentCellValue(item?.create_by),
        closingStatus: closingStatus,
        checkInStatus: formatAppointmentCellValue(
          showAsVisited ? "Visited" : "",
        ),
        visitingScore: formatAppointmentCellValue(item?.lead_score),
      };
    });

    // Add rows to the worksheet
    worksheet.addRows(data);

    worksheet.getRow(1).font = { bold: true };
    // Generate the Excel file
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob object with the Excel file content
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create a temporary download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "appointments_report.xlsx"; // The filename of the Excel file
    link.click();
  };
  return (
    <div className="page-body">
      <Breadcrumb
        title={strings.appointment}
        titleactive={strings.appointmentList}
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12 col-xl-12">
            <div className="row">
              <div className="col-sm-12">
                <div className="card">
                  <div className="card-body">
                    <form className="theme-form">
                      <div className="row">
                        <div className="col-md-4">
                          <span className="cardText">{strings.startDate}</span>
                          <div
                            className="input-group date"
                            id="dt-date"
                            data-target-input="nearest"
                          >
                            <input
                              className="form-control datetimepicker-input digits"
                              type="date"
                              data-target="#dt-date"
                              name="start_date"
                              value={form.start_date}
                              onChange={handleInputField}
                              // min={form.start_date}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <span className="cardText">{strings.endDate}</span>
                          <div
                            className="input-group date"
                            id="dt-date"
                            data-target-input="nearest"
                          >
                            <input
                              className="form-control datetimepicker-input digits"
                              type="date"
                              name="end_date"
                              value={form.end_date}
                              onChange={handleInputField}
                              min={form.start_date}
                              // min={form.end_date}
                              disabled={endDateDisable}
                            />
                          </div>
                          {formVaild.end_date && (
                            <span style={{ color: "red" }}>
                              {formVaild.end_date}
                            </span>
                          )}
                        </div>
                        <div className="col-md-4">
                          <span className="cardText">
                            {strings.searchByCustomerName}
                          </span>
                          <div
                            className="input-group date"
                            id="dt-date"
                            data-target-input="nearest"
                          >
                            <input
                              className="form-control datetimepicker-input digits"
                              placeholder={strings.searchCustomerName}
                              type="text"
                              autoComplete="off"
                              name="customer_name"
                              value={form.customer_name}
                              onChange={handleInputField}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row mt-4">
                        <div className="col-md-4">
                          <span className="cardText">
                            {strings.searchByStatus}
                          </span>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="status"
                            value={form.status}
                            onChange={handleInputField}
                          >
                            <option value={""}>{strings.selectstatus}</option>
                            <option value={1}>{strings.panding}</option>
                            <option value={2}>{strings.revisit}</option>
                            <option value={3}>{strings.actualvisit}</option>
                            <option value={4}>{strings.visitcancelled}</option>
                            <option value={5}>{strings.rescheduled}</option>
                            <option value={6}>{strings.notfitforseal}</option>
                            {isAdmin && (
                              <option value={12}>Revisit Completed</option>
                            )}
                          </select>
                        </div>
                        <div className="col-md-4">
                          <span className="cardText">
                            {strings.searchByPropertyName}
                          </span>
                          <Select
                            isSearchable
                            isClearable
                            onInputChange={(e) => {
                              if (isAdmin) {
                                if (e.length > 1) {
                                  dispatch(
                                    getReportPropertyList({
                                      search_site: e,
                                      isLoadMore: true,
                                    }),
                                  );
                                } else {
                                  dispatch(
                                    getReportPropertyList({
                                      limit: "",
                                      offset: 0,
                                      isLoadMore: true,
                                    }),
                                  );
                                }
                              }
                            }}
                            placeholder={strings.searchByPropertyName}
                            options={propertyList}
                            getOptionLabel={(option) =>
                              `${option.property_title}`
                            }
                            getOptionValue={(option) => option._id}
                            name="site"
                            value={form.site}
                            onChange={(e) => {
                              setForm({ ...form, site: e });
                            }}
                          />
                        </div>
                        <div className="col-md-4 ">
                          <span className="cardText">
                            {strings.searchByMobileNumber}
                          </span>
                          <input
                            className="form-control datetimepicker-input digits"
                            type="text"
                            autoComplete="off"
                            placeholder={strings.searchMobileNumber}
                            name="search_by_mobile_number"
                            value={form.search_by_mobile_number}
                            onChange={handleInputField}
                            maxLength="10"
                          />
                        </div>
                      </div>

                      <div className="row mt-4">
                        <div className="col-md-4">
                          <span className="cardText">{strings.leadStatus}</span>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="qualified"
                            onChange={handleInputField}
                            value={form.qualified}
                          >
                            <option value={""}>
                              {strings.selectLeadStatus}
                            </option>
                            <option value={true}>{strings.qualified}</option>
                            <option value={false}>{strings.unQualified}</option>
                          </select>
                        </div>
                        <div className="col-md-4">
                          <span className="cardText">
                            {strings.leadPriority}
                          </span>
                          <select
                            className="form-control digits"
                            id="formcontrol-select1"
                            name="lead_priority"
                            onChange={handleInputField}
                            value={form.lead_priority}
                          >
                            <option value={""}>
                              {strings.selectLeadPriority}
                            </option>
                            <option value={"hot"}>{strings.hot}</option>
                            <option value={"warm"}>{strings.warm}</option>
                            <option value={"cold"}>{strings.cold}</option>
                          </select>
                        </div>
                        <div className="col-md-4 mt-4">
                          <div className="form-group d-flex justify-content-end">
                            <button
                              className="btn btn-warning mr-4"
                              type="button"
                              onClick={filterDetail}
                            >
                              {strings.search}
                            </button>
                            <button
                              className="btn btn-dark"
                              type="button"
                              onClick={filterReset}
                            >
                              {strings.reset}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* <div className="row pt-3"></div> */}
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center w-100 pt-3 mb-5">
              {!isAdmin && create && (
                <div className="row pt-3">
                  <div className="col-auto">
                    <div className="form-group">
                      <button
                        className="btn btn-color"
                        type="button"
                        onClick={() => navigatpage("/add_appointment")}
                      >
                        {strings.addAppointment}
                      </button>
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="form-group">
                      <button
                        className="btn btn-color"
                        type="button"
                        onClick={() => navigatpage("/today_appointments")}
                      >
                        {strings.todayAppointments}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {appointmentData.length > 0 && (
                <div className="d-flex justify-content-end ">
                  <button className="btn btn-primary" onClick={exportToExcel}>
                    Export to Excel
                  </button>
                </div>
              )}
            </div>

            <div className="row">
              <div className="col-sm-12 col-xl-12">
                <div className="card">
                  <div className="card-header">
                    <h5>{strings.listofappointment}</h5>
                  </div>
                  <div className="table-responsive">
                    {!response1 && isAdmin ? (
                      <div className="text-center mt-4 mb-3">
                        Please apply filter to view data
                      </div>
                    ) : !response && !response1 ? (
                      <div className="text-center mt-4 mb-3">Loading...</div>
                    ) : isAdmin && res === true ? (
                      <div className="text-center mt-4 mb-3">
                        Please apply filter to view data
                      </div>
                    ) : appointmentData.length === 0 ? (
                      <div className="text-center mt-4 mb-3">
                        No data available
                      </div>
                    ) : (
                      <table className="table table-border-horizontal">
                        <thead>
                          <tr className="text-center">
                            <th scope="col">{strings.createdDate}</th>
                            <th scope="col">
                              {revistCheck
                                ? strings.siteRevisitVisitTimeDate
                                : strings.siteVisitTimeDate}
                            </th>

                            {/* <th scope="col">{strings.siteVisitTimeDate}</th> */}
                            <th scope="col">{strings.property_name}</th>
                            <th scope="col">{strings.customerName}</th>
                            {/* <th scope="col">{strings.pickup}</th> */}
                            <th scope="col">{strings.source} </th>
                            <th scope="col">{strings.closingStatus}</th>
                            <th scope="col">{strings.checkInStatus}</th>
                            <th scope="col">{strings.visitingScore}</th>
                            <th scope="col">{strings.action}</th>
                          </tr>
                        </thead>
                        <tbody className="text-center">
                          {appointmentData.map((item, index) => {
                            const chekin = hasCheckin(item?.checkin_status);
                            // Also check site_visit_status from mapping - if it's "Visited", "Completed", or "Site Visit Done", show as visited
                            const siteVisitStatus = item?.site_visit_status;
                            const isVisitedFromMapping =
                              siteVisitStatus === "Visited" ||
                              siteVisitStatus === "Completed" ||
                              siteVisitStatus === "Site Visit Done";
                            const showAsVisited =
                              chekin || isVisitedFromMapping;
                            return (
                              <tr key={index}>
                                <td>
                                  {formatAppointmentCellValue(
                                    item.createdDate
                                      ? moment(item.createdDate).format(
                                          "DD-MM-yyyy hh:mm A",
                                        )
                                      : "",
                                  )}
                                </td>
                                <td>
                                  {formatAppointmentCellValue(
                                    item.appointment_date
                                      ? moment(item.appointment_date)
                                          .utc()
                                          .format("DD-MM-yyyy") +
                                          " & " +
                                          (item.appointment_time || "")
                                      : "",
                                  )}
                                </td>
                                <td style={{ inlineSize: "130px" }}>
                                  {formatAppointmentCellValue(
                                    item?.property_title,
                                  )}
                                </td>
                                <td style={{ inlineSize: "130px" }}>
                                  {formatAppointmentCellValue(
                                    `${item?.customer_first_name || ""} ${item?.customer_last_name || ""}`.trim(),
                                  )}
                                </td>
                                <td style={{ inlineSize: "130px" }}>
                                  {formatAppointmentCellValue(item?.create_by)}
                                </td>
                                <td
                                  style={{
                                    color: getClosingStatusTextColor(
                                      getClosingStatusLabel(
                                        item,
                                        showAsVisited,
                                      ),
                                    ),
                                    fontWeight: "600",
                                  }}
                                >
                                  {formatAppointmentCellValue(
                                    getClosingStatusLabel(item, showAsVisited),
                                  )}
                                </td>
                                <td
                                  style={{
                                    color:
                                      getCheckInStatusTextColor(showAsVisited),
                                    fontWeight: "600",
                                  }}
                                >
                                  {formatAppointmentCellValue(
                                    showAsVisited ? "Visited" : "",
                                  )}
                                </td>
                                <td>
                                  {formatAppointmentCellValue(item?.lead_score)}
                                </td>
                                <td className="p-2">
                                  {view && (
                                    <i
                                      className="fa fa-eye theme-fa-icon p-1"
                                      aria-hidden="true"
                                      title={"View Appointment"}
                                      onClick={() =>
                                        navigatpage(
                                          `/viewappointment/${item._id}`,
                                        )
                                      }
                                    ></i>
                                  )}
                                  {item.status === 1 && !isAdmin && edit && (
                                    <i
                                      className="fa fa-edit theme-fa-icon p-1"
                                      aria-hidden="true"
                                      title="Edit Appointment"
                                      onClick={() =>
                                        navigatpage(
                                          `/editappointment/${item._id}`,
                                        )
                                      }
                                    ></i>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* {!isAdmin && (
                    <Pagination
                      totalCount={appointmentListSelector?.response?.total_data}
                      listData={appointmentData}
                      onPageChange={handlePageClick}
                      itemOffset={page}
                      setItemOffset={setPage}
                    />
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Appointment;
